import { useState, useEffect } from "react";
import axios from "axios";
import { HandCoins, Info, FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API } from "@/context/AuthContext";
import { inr } from "@/pages/Dashboard";
import { toast } from "sonner";

const TABS = [
  { key: "ap", label: "AP — Vendors", partyLabel: "Vendor" },
  { key: "ar", label: "AR — Customers", partyLabel: "Customer" },
];

export default function AdvanceRequests() {
  const [ptype, setPtype] = useState("ap");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/advance-requests/${ptype}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [ptype]);

  const active = TABS.find((t) => t.key === ptype);

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/advance-requests/${ptype}/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${ptype}-advance-requests.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 200);
    } catch {
      toast.error("Could not generate the export. Please try again.");
    }
  };

  return (
    <div className="space-y-6" data-testid="advance-requests-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2.5">
            <HandCoins className="h-7 w-7 text-sky-700" /> Pending Advance Requests
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Down payment requests (SAP Special GL "F") — statistical noted items, excluded from AP/AR balances and ageing. Shown here for visibility only.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="advance-export-pdf"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
          <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="advance-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-1.5" />Excel</Button>
          <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="advance-export-csv"><FileDown className="h-4 w-4 mr-1.5" />CSV</Button>
          <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-1" data-testid="advance-requests-tabs">
            {TABS.map((t) => (
              <Button
                key={t.key}
                variant={ptype === t.key ? "default" : "ghost"}
                size="sm"
                onClick={() => setPtype(t.key)}
                data-testid={`advance-tab-${t.key}`}
                className={ptype === t.key ? "bg-sky-700 hover:bg-sky-800 text-white" : "text-slate-600"}
              >
                {t.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wider text-slate-400">Pending Requests</p>
            <p className="text-3xl font-black text-slate-900 mt-1" data-testid="advance-count">{data?.count ?? 0}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-5">
            <p className="text-xs uppercase tracking-wider text-slate-400">Total Requested Value</p>
            <p className="text-3xl font-black text-slate-900 mt-1" data-testid="advance-total">{inr(data?.total || 0)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-bold text-slate-900">{active.label}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {loading ? (
            <div className="animate-pulse text-slate-400 text-sm py-6">Loading advance requests…</div>
          ) : (
            <Table data-testid="advance-requests-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">{active.partyLabel}</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Document No</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">Due</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500">WBS / PO</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((it) => (
                  <TableRow key={`${it.party_code}-${it.doc_no}`} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`advance-row-${it.doc_no}`}>
                    <TableCell className="text-sm text-slate-800">
                      <span className="font-medium">{it.party_name}</span>
                      <span className="block text-xs text-slate-400 font-mono">{it.party_code}</span>
                    </TableCell>
                    <TableCell className="text-sm font-mono text-slate-700">{it.doc_no}</TableCell>
                    <TableCell className="text-sm text-slate-600">{it.date}</TableCell>
                    <TableCell className="text-sm text-slate-600">{it.due_date || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-[220px] truncate">
                      {[it.wbs_element, it.po_number].filter((x) => x && x !== "UNASSIGNED").join(" · ") || "—"}
                    </TableCell>
                    <TableCell className="text-right text-sm font-semibold text-slate-900">{inr(it.amount)}</TableCell>
                  </TableRow>
                ))}
                {(!data || data.count === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-sm text-slate-400 py-8">
                      No pending advance requests found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
            <Info className="h-3.5 w-3.5" /> These are noted items (F-47 / down payment requests). They do not affect the vendor/customer balance until an actual down payment is posted.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
